import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NoteRichTextEditor from "./NoteRichTextEditor";
import NoteAttachmentUpload from "./NoteAttachmentUpload";
import NoteAttachmentIndicator from "./NoteAttachmentIndicator";
import {
  filterTopicsBySearch,
  getSubjectIdsForNames,
  mergeNoteTags,
  partitionNoteTags,
  pruneTopicsForSubjects,
} from "../../data/myNotesSubjectTopics";
import { getMyNotesTopics } from "../../services/myNotesService";
import {
  deriveNoteTitle,
  getNoteAttachmentCount,
  getSubjectTagStyle,
  getTagDisplayLabel,
} from "../../utils/myNotes";

const DEFAULT_FALLBACK_TAG = "General";
const TITLE_MAX_LINES = 3;

const stripHtml = (value = "") =>
  value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

function EditNoteModal({ note, isSaving, subjects = [], subjectsLoading = false, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("<p></p>");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [customTags, setCustomTags] = useState([]);
  const [topicSearch, setTopicSearch] = useState("");
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [customTagInput, setCustomTagInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentIdsToRemove, setAttachmentIdsToRemove] = useState([]);
  const [availableTopicNames, setAvailableTopicNames] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState("");
  const titleInputRef = useRef(null);
  const topicsClassifiedRef = useRef(false);

  const resizeTitleInput = useCallback(() => {
    const element = titleInputRef.current;
    if (!element) {
      return;
    }

    element.style.height = "auto";
    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight) || 29;
    const maxHeight = lineHeight * TITLE_MAX_LINES;
    const nextHeight = Math.min(element.scrollHeight, maxHeight);
    element.style.height = `${nextHeight}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    if (!note) {
      return;
    }

    const { subjects: parsedSubjects, topics, custom } = partitionNoteTags(
      note.tags || [],
      subjects,
    );
    setTitle(note.title || "");
    setContent(note.content || "<p></p>");
    setSelectedSubjects(parsedSubjects);
    setSelectedTopics(topics);
    setCustomTags(custom);
    setTopicSearch("");
    setCustomTagInput("");
    setAttachments(note.attachments || []);
    setAttachmentIdsToRemove([]);
    setAvailableTopicNames([]);
    setTopicsError("");
    topicsClassifiedRef.current = false;
  }, [note, subjects]);

  useEffect(() => {
    let cancelled = false;
    const subjectIds = getSubjectIdsForNames(selectedSubjects, subjects);

    if (!subjectIds.length) {
      setAvailableTopicNames([]);
      setTopicsLoading(false);
      setTopicsError("");
      setSelectedTopics([]);
      return undefined;
    }

    setTopicsLoading(true);
    setTopicsError("");

    getMyNotesTopics(subjectIds)
      .then(({ topicNames }) => {
        if (cancelled) {
          return;
        }
        setAvailableTopicNames(topicNames);
        setSelectedTopics((current) => pruneTopicsForSubjects(current, topicNames));
        setTopicsLoading(false);

        if (!topicsClassifiedRef.current && note) {
          const { subjects: parsedSubjects, topics, custom } = partitionNoteTags(
            note.tags || [],
            subjects,
            topicNames,
          );
          setSelectedSubjects(parsedSubjects);
          setSelectedTopics(topics);
          setCustomTags(custom);
          topicsClassifiedRef.current = true;
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error("Failed to load note topics", error);
        setAvailableTopicNames([]);
        setTopicsError("Could not load topics for the selected subject(s).");
        setTopicsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSubjects, subjects, note]);

  useEffect(() => {
    resizeTitleInput();
  }, [title, note, resizeTitleInput]);

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
    requestAnimationFrame(resizeTitleInput);
  };

  const handleTitleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const filteredTopics = useMemo(
    () => filterTopicsBySearch(availableTopicNames, topicSearch),
    [availableTopicNames, topicSearch],
  );

  const allSelectedTags = useMemo(
    () => mergeNoteTags({ subjects: selectedSubjects, topics: selectedTopics, custom: customTags }),
    [selectedSubjects, selectedTopics, customTags],
  );

  if (!note) {
    return null;
  }

  const isNewNote = !note.id;
  const hasContent = stripHtml(content).length > 0;
  const attachmentCount = getNoteAttachmentCount({ attachments });

  const handleAttachmentsChange = (nextAttachments) => {
    const originalAttachments = note.attachments || [];
    const removedIds = originalAttachments
      .filter(
        (existing) =>
          !nextAttachments.some((attachment) => attachment.id === existing.id),
      )
      .map((attachment) => attachment.id);

    const restoredIds = originalAttachments
      .filter((existing) =>
        nextAttachments.some((attachment) => attachment.id === existing.id),
      )
      .map((attachment) => attachment.id);

    if (removedIds.length || restoredIds.length) {
      setAttachmentIdsToRemove((current) => {
        const next = new Set(current);
        removedIds.forEach((id) => next.add(id));
        restoredIds.forEach((id) => next.delete(id));
        return [...next];
      });
    }

    setAttachments(nextAttachments);
  };

  const normalizeTag = (value) =>
    (value || "")
      .trim()
      .replace(/\s+/g, " ");

  const toggleSubject = (subjectName) => {
    setSelectedSubjects((current) => {
      const isSelected = current.some(
        (name) => name.toLowerCase() === subjectName.toLowerCase(),
      );
      const nextSubjects = isSelected
        ? current.filter((name) => name.toLowerCase() !== subjectName.toLowerCase())
        : [...current, subjectName];

      return nextSubjects;
    });
  };

  const toggleTopic = (topicName) => {
    setSelectedTopics((current) => {
      const isSelected = current.some(
        (topic) => topic.toLowerCase() === topicName.toLowerCase(),
      );
      if (isSelected) {
        return current.filter((topic) => topic.toLowerCase() !== topicName.toLowerCase());
      }
      return [...current, topicName];
    });
  };

  const removeSelectedTag = (tagToRemove) => {
    const lower = tagToRemove.toLowerCase();
    setSelectedSubjects((current) =>
      current.filter((slug) => slug.toLowerCase() !== lower),
    );
    setSelectedTopics((current) =>
      current.filter((topic) => topic.toLowerCase() !== lower),
    );
    setCustomTags((current) =>
      current.filter((tag) => tag.toLowerCase() !== lower),
    );
  };

  const addCustomTag = () => {
    const rawParts = customTagInput.split(/[;,]/);
    const newTags = [];
    for (const rawPart of rawParts) {
      const normalizedTag = normalizeTag(rawPart);
      if (!normalizedTag) {
        continue;
      }

      const lower = normalizedTag.toLowerCase();
      const alreadySelected = allSelectedTags.some(
        (tag) => tag.toLowerCase() === lower,
      );
      if (alreadySelected) {
        continue;
      }

      newTags.push(normalizedTag);
    }

    if (newTags.length) {
      setCustomTags((current) => [...current, ...newTags]);
    }
    setCustomTagInput("");
  };

  const handleCustomTagKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addCustomTag();
    }
  };

  const handleSave = () => {
    const mergedTags = mergeNoteTags({
      subjects: selectedSubjects,
      topics: selectedTopics,
      custom: customTags.map((tag) => normalizeTag(tag)).filter(Boolean),
    });

    onSave?.({
      ...note,
      title: deriveNoteTitle(title.replace(/\s*\n+\s*/g, " "), content, { attachments }),
      content,
      tags: mergedTags.length ? mergedTags : [DEFAULT_FALLBACK_TAG],
      attachments,
      attachmentIdsToRemove,
    });
  };

  const hasAttachments = attachments.length > 0;
  const canSave =
    !isSaving && (hasContent || title.trim() || hasAttachments || !isNewNote);

  const renderTagPill = (tag) => {
    const tagStyle = getSubjectTagStyle(tag);
    return (
      <button
        key={tag}
        type="button"
        className="my-notes-edit-tag-pill"
        style={tagStyle}
        onClick={() => removeSelectedTag(tag)}
        title={`Remove ${getTagDisplayLabel(tag)}`}
        disabled={isSaving}
      >
        <span>{getTagDisplayLabel(tag)}</span>
        <span aria-hidden="true">×</span>
      </button>
    );
  };

  return (
    <div className="my-notes-modal-overlay" role="presentation">
      <div
        className="my-notes-modal my-notes-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-note-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="my-notes-modal-header my-notes-modal-header-with-title">
          <textarea
            id="edit-note-title"
            ref={titleInputRef}
            className="my-notes-modal-note-title-input"
            rows={1}
            value={title}
            onChange={handleTitleChange}
            onKeyDown={handleTitleKeyDown}
            placeholder={isNewNote ? "Note title" : "Edit note title"}
            aria-label="Note title"
            disabled={isSaving}
          />
          <div className="my-notes-modal-header-actions">
            {attachmentCount > 0 ? (
              <NoteAttachmentIndicator count={attachmentCount} />
            ) : null}
            <button
              type="button"
              className="my-notes-modal-close"
              aria-label="Close editor"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="my-notes-modal-body">
          <NoteRichTextEditor
            content={content}
            onContentChange={setContent}
            hideTitle
          />

          <div className="my-notes-edit-tags">
            {allSelectedTags.length > 0 ? (
              <div className="my-notes-edit-tags-selected">
                {allSelectedTags.map((tag) => renderTagPill(tag))}
              </div>
            ) : (
              <p className="my-notes-edit-tags-empty">
                No tags selected. General will be used when you save.
              </p>
            )}

            <button
              type="button"
              className="my-notes-edit-tags-header"
              onClick={() => setTagsExpanded((prev) => !prev)}
              aria-expanded={tagsExpanded}
              aria-label={tagsExpanded ? "Collapse tag options" : "Expand tag options"}
            >
              <p className="my-notes-edit-tags-label">
                {tagsExpanded ? "Tags" : "Add more tags"}
              </p>
              <svg
                className={`my-notes-edit-tags-chevron ${tagsExpanded ? "is-expanded" : ""}`}
                viewBox="0 0 24 24"
                width="16"
                height="16"
                aria-hidden="true"
              >
                <path
                  d="M6 9l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {tagsExpanded && (
              <>
                <div className="my-notes-edit-tags-section">
                  <p className="my-notes-edit-tags-subsection-label">Subject</p>
                  {subjectsLoading ? (
                    <p className="my-notes-edit-tags-hint">Loading subjects...</p>
                  ) : subjects.length === 0 ? (
                    <p className="my-notes-edit-tags-hint">Subjects could not be loaded.</p>
                  ) : (
                    <div className="my-notes-edit-tags-suggestions">
                      {subjects.map((subject) => {
                        const isSelected = selectedSubjects.some(
                          (name) => name.toLowerCase() === subject.name.toLowerCase(),
                        );
                        const tagStyle = getSubjectTagStyle(subject.name);
                        return (
                          <button
                            key={subject.id}
                            type="button"
                            className={`my-notes-filter-tag ${isSelected ? "is-selected" : ""}`}
                            style={
                              isSelected
                                ? {
                                    backgroundColor: tagStyle.backgroundColor,
                                    color: tagStyle.color,
                                    borderColor: tagStyle.borderColor,
                                  }
                                : undefined
                            }
                            title={subject.name}
                            onClick={() => toggleSubject(subject.name)}
                            disabled={isSaving}
                          >
                            {subject.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="my-notes-edit-tags-section">
                  <p className="my-notes-edit-tags-subsection-label">Topics</p>
                  {selectedSubjects.length === 0 ? (
                    <p className="my-notes-edit-tags-hint">Select a subject to search and add topics.</p>
                  ) : topicsLoading ? (
                    <p className="my-notes-edit-tags-hint">Loading topics...</p>
                  ) : topicsError ? (
                    <p className="my-notes-edit-tags-hint is-error">{topicsError}</p>
                  ) : (
                    <>
                      <label className="my-notes-edit-tags-search">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <circle cx="11" cy="11" r="7" />
                          <path d="M20 20l-3.5-3.5" />
                        </svg>
                        <input
                          type="search"
                          className="my-notes-edit-tags-input"
                          value={topicSearch}
                          onChange={(event) => setTopicSearch(event.target.value)}
                          placeholder="Search topics for selected subject(s)"
                          aria-label="Search topics"
                          disabled={isSaving}
                        />
                      </label>

                      {filteredTopics.length > 0 ? (
                        <div className="my-notes-edit-tags-suggestions my-notes-edit-tags-topic-list">
                          {filteredTopics.map((topicName) => {
                            const isSelected = selectedTopics.some(
                              (topic) => topic.toLowerCase() === topicName.toLowerCase(),
                            );
                            return (
                              <button
                                key={topicName}
                                type="button"
                                className={`my-notes-filter-tag my-notes-filter-tag-topic ${
                                  isSelected ? "is-selected" : ""
                                }`}
                                title={topicName}
                                onClick={() => toggleTopic(topicName)}
                                disabled={isSaving}
                              >
                                {topicName}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="my-notes-edit-tags-hint">
                          {topicSearch.trim()
                            ? "No topics match your search for the selected subject(s)."
                            : "No topics available for the selected subject(s)."}
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div className="my-notes-edit-tags-section">
                  <p className="my-notes-edit-tags-subsection-label">Add tags</p>
                  <div className="my-notes-edit-tags-input-row">
                    <input
                      type="text"
                      className="my-notes-edit-tags-input"
                      value={customTagInput}
                      onChange={(event) => setCustomTagInput(event.target.value)}
                      onKeyDown={handleCustomTagKeyDown}
                      placeholder="Add tags separated by ;"
                      aria-label="Add custom tag"
                      disabled={isSaving}
                    />
                    <button
                      type="button"
                      className="my-notes-edit-tags-add-btn"
                      onClick={addCustomTag}
                      disabled={isSaving || !customTagInput.trim()}
                    >
                      Add Tag
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <NoteAttachmentUpload
            attachments={attachments}
            onChange={handleAttachmentsChange}
            disabled={isSaving}
          />
        </div>

        <div className="my-notes-edit-footer">
          <button
            type="button"
            className="my-notes-save-button"
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditNoteModal;
